import Link from "next/link";
import React from "react";
import Image from "next/image";
import { motion, Variants, AnimatePresence } from "framer-motion";
import { getFileUrl } from "@/shared";
import { useSearchParams } from "next/navigation";

interface NewsCardProps {
  item: any;
  className?: string;
}

const categoryColors = {
  모집: "var(--color-label-blue)",
  공지: "var(--color-label-red)",
  소식: "var(--color-label-gray)",
};

export const NewsCardBase: React.FC<NewsCardProps> = ({
  item,
  className = "",
}) => {
  const searchParams = useSearchParams();

  const categoryColor = item.common_category
    ? categoryColors[item.common_category.name as keyof typeof categoryColors]
    : "var(--color-label-gray)";

  return (
    <Link
      href={{
        pathname: `/app/news/${item.id}`,
        query: searchParams?.toString() || "",
      }}
      className={`block h-full  ${className}`}
    >
      <motion.div
        className="flex flex-col h-full bg-white rounded-4xl overflow-hidden   border border-[#eee]"
        style={{ boxShadow: "2px 4px 21.7px rgba(99, 99, 99, 0.11)" }}
        initial="offscreen"
        whileInView="onscreen"
        viewport={{ once: true, amount: 0.1 }}
        whileHover={{
          y: -5, // 위로 5px 살짝 올리기
          boxShadow: "4px 8px 30px rgba(99,99,99,0.15)",
          transition: { type: "spring", stiffness: 150, damping: 20 },
        }}
        variants={{
          offscreen: { opacity: 0.1, y: 20 },
          onscreen: {
            opacity: 1,
            y: 0,
            transition: { duration: 1.8, ease: [0.22, 1, 0.36, 1] }, // 천천히 올라오는 느낌
          },
        }}
      >
        <div className="py-6 px-6 md:py-11 md:px-8 flex flex-col justify-between">
          {/* 카테고리 라벨 */}
          {item.common_category && (
            <div
              className="text-white self-baseline md:text-base md:leading-6 pt-1.5 pb-1 px-3 rounded-full mb-3.5 text-sm leading-5 tracking-tight"
              style={{
                backgroundColor: categoryColor,
                fontFamily: '"pre-Medium", sans-serif',
              }}
            >
              {item.common_category.name}
            </div>
          )}

          {/* 제목 */}
          <h3
            className="md:text-2xl md:leading-9 text-lg leading-7 line-clamp-2 mb-4 min-h-auto md:min-h-20 text-black-200 tracking-tight"
            style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
          >
            {item.title}
          </h3>

          {/* 날짜 */}
          <p
            className="text-lightGray-10 md:text-base md:leading-7 tracking-tight text-sm leading-5"
            style={{ fontFamily: '"pre-Regular", sans-serif' }}
          >
            {new Date(item.created_at).toLocaleDateString("ko")}
          </p>
        </div>
        {/* 썸네일  */}
        <Image
          src={
            item.cover?.file_url
              ? getFileUrl(item.cover?.file_url)
              : "/assets/images/default_image.svg"
          }
          alt="thumbnail"
          width={800}
          height={240}
          className={`w-full h-[180px] md:h-[240px] object-cover ${
            item.cover?.file_url ? "object-top md:object-top" : "object-center"
          }`}
        />
      </motion.div>
    </Link>
  );
};
